/**
 * Persistente voortgang (§19). Eén save onder `chakra-architect-save-v1`:
 * voltooide chakra's, Reisdagboek-entries (§16) en geziene Codex/Archief-items.
 * Gebruikt de veilige opslag-wrapper (fallback in-memory).
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Origin } from '../data/types';
import type { Houding } from '../data/vn';
import { veiligeOpslag, opslagWerkt } from './veiligeOpslag';

export interface JournaalEntry {
  chakra: Origin;
  baasNaam: string;
  soort: 'houding' | 'reflectie';
  tekst: string;
}

interface ProgressStore {
  voltooideChakras: Origin[];
  reisdagboek: JournaalEntry[];
  codexGezien: string[];

  voltooiChakra: (c: Origin) => void;
  logHouding: (chakra: Origin, baasNaam: string, houding: Houding, regel: string) => void;
  logReflectie: (chakra: Origin, baasNaam: string, regel: string) => void;
  markeerGezien: (id: string) => void;
  resetVoortgang: () => void;
}

export const useProgressStore = create<ProgressStore>()(
  persist(
    (set) => ({
      voltooideChakras: [],
      reisdagboek: [],
      codexGezien: [],

      voltooiChakra: (c) =>
        set((s) => (s.voltooideChakras.includes(c) ? s : { voltooideChakras: [...s.voltooideChakras, c].sort((a, b) => a - b) })),

      logHouding: (chakra, baasNaam, houding, regel) =>
        set((s) => ({ reisdagboek: [...s.reisdagboek, { chakra, baasNaam, soort: 'houding', tekst: `${houding} — ${regel}` }] })),

      logReflectie: (chakra, baasNaam, regel) =>
        set((s) => ({ reisdagboek: [...s.reisdagboek, { chakra, baasNaam, soort: 'reflectie', tekst: regel }] })),

      markeerGezien: (id) => set((s) => (s.codexGezien.includes(id) ? s : { codexGezien: [...s.codexGezien, id] })),

      resetVoortgang: () => set({ voltooideChakras: [], reisdagboek: [], codexGezien: [] }),
    }),
    {
      name: 'chakra-architect-save-v1',
      storage: createJSONStorage(() => veiligeOpslag),
    }
  )
);

/** Of een chakra speelbaar (ontgrendeld) is: ch1 altijd, anders is de vorige voltooid. */
export function isOntgrendeld(c: Origin, voltooid: Origin[]): boolean {
  return c === 1 || voltooid.includes((c - 1) as Origin);
}

/** Is persistente opslag beschikbaar? (voor de "wordt niet bewaard"-melding) */
export const voortgangWordtBewaard = opslagWerkt;
